import { Countless, CreditCardPayment, FligthDeals, MobileFirst, NoRedirection, PurchaseSecurity, QuickEasy } from '@enuygun/icons'
import { useRef, ReactNode } from 'react'

import Slider from 'components/Slider'
import SnapCarousel from 'components/SnapCarousel'
import { useAppState } from 'context/AppContext'
import { i18n, useTranslation } from 'next-i18next'

import { informationCardDataTR } from './dataTR'
import * as S from './WhyChoose.styled'
import { useDeviceType } from 'hooks/useDeviceType'

interface WhyChooseProps {
  res: any[]
  isTablet?: boolean
}

const brandTestIdPrefix = (market?: string) => (market === 'tr' ? 'enuygun' : 'wingie')

const brandNameSuffix = (market?: string) => (market === 'tr' ? 'Enuygun' : 'Wingie')

const whyChooseCardTestId = (market: string | undefined, key: number | string) => `${brandTestIdPrefix(market)}-homepage-whyUse${brandNameSuffix(market)}-${key}`

const isRTL = (): boolean => {
  const lang = i18n?.language
  const dirLang = lang?.includes('-') ? lang.split('-')[1] : lang
  return i18n?.dir(dirLang) === 'rtl'
}

const informationCardDataWingie = {
  plane: <Countless width={60} height={60} />,
  ticket: <FligthDeals width={60} height={60} />,
  mobile: <MobileFirst width={60} height={60} />,
  trophy: <QuickEasy width={60} height={60} />,
  flag: <NoRedirection width={60} height={60} />,
  secure: <PurchaseSecurity width={60} height={60} />,
  installment: <CreditCardPayment width={60} height={60} />,
}

type CarouselMode = 'tablet' | 'mobile' | 'desktop'

const getCarouselMode = (isTabletFinal: boolean, isMobile?: boolean | null): CarouselMode => {
  if (isTabletFinal) return 'tablet'
  if (isMobile) return 'mobile'
  return 'desktop'
}

type GetCarouselContentParams = {
  mode: CarouselMode
  rtl: boolean
  isTablet: boolean
  prefix: string
  brandSuffix: string
  cards: ReactNode
}

const getCarouselContent = ({ mode, rtl, isTablet, prefix, brandSuffix, cards }: GetCarouselContentParams): ReactNode => {
  if (mode === 'tablet') {
    return (
      <S.SnapCarouselWrapper isRTL={rtl} isTablet={isTablet} data-testid={`${prefix}-homepage-whyUse-carousel`}>
        <SnapCarousel
          options={{
            perPage: 2,
            gap: 16,
            hasLastChildPadding: false,
          }}
          dataTestId={`whyUse${brandSuffix}-carousel`}
          containerWidth={1120}
          isMobile={false}
        >
          {cards}
        </SnapCarousel>
      </S.SnapCarouselWrapper>
    )
  }

  if (mode === 'mobile') {
    return (
      <S.SnapCarouselWrapper isRTL={rtl} data-testid={`${prefix}-homepage-whyUse-carousel`}>
        <SnapCarousel
          options={{
            perPage: 1.34,
            gap: 16,
            hasLastChildPadding: true,
          }}
          dataTestId={`whyUse${brandSuffix}-carousel`}
          containerWidth={1120}
          isMobile={true}
        >
          {cards}
        </SnapCarousel>
      </S.SnapCarouselWrapper>
    )
  }

  return (
    <Slider isRTL={rtl} options={{ perPage: 4, arrows: false, gap: 16, padding: 16 }} dataTestId={`whyUse${brandSuffix}-slider`}>
      {cards}
    </Slider>
  )
}

const WhyChoose = ({ res, isTablet = false }: WhyChooseProps) => {
  const { t } = useTranslation('common')
  const { market } = useAppState()
  const { isMobile, isTablet: isTabletDevice } = useDeviceType()
  const isTabletFinal = !!(isTablet || isTabletDevice)
  let isWingie = market !== 'tr'
  const prefix = brandTestIdPrefix(market)
  const brandSuffix = brandNameSuffix(market)
  const rtl = isRTL()

  const whyChooseContainerRef = useRef<HTMLDivElement>(null)

  const data = isWingie ? res[3]?.propositions : informationCardDataTR

  if (!data || data.length === 0) {
    return null
  }

  const renderWhyChooseCard = ({ title, content, icon }, key) => (
    <S.WhyChooseCard key={key} isWingie={isWingie} data-testid={whyChooseCardTestId(market, key)}>
      {isWingie ? informationCardDataWingie[icon] : icon}
      <div data-testid={`${whyChooseCardTestId(market, key)}-container`}>
        <S.WhyChooseCardHeading data-testid={`${whyChooseCardTestId(market, key)}-title`}>{title}</S.WhyChooseCardHeading>
        <S.WhyChooseCardText data-testid={`${whyChooseCardTestId(market, key)}-paragraf`}>{content}</S.WhyChooseCardText>
      </div>
    </S.WhyChooseCard>
  )

  const cards = data?.map(renderWhyChooseCard)
  const mode = getCarouselMode(isTabletFinal, isMobile)

  return (
    <S.WhyChooseContainer ref={whyChooseContainerRef}>
      <S.Content data-testid={`${prefix}-homepage-whyUse-container`}>
        <S.WhyChooseHeading data-testid={`${prefix}-homepage-whyUse-heading-text`}>{t('whyChoose.heading')}</S.WhyChooseHeading>
        {getCarouselContent({ mode, rtl, isTablet, prefix, brandSuffix, cards })}
      </S.Content>
    </S.WhyChooseContainer>
  )
}

export default WhyChoose
